"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  MinusCircle,
  ChevronDown,
  ChevronRight,
  MessageSquarePlus,
  X,
  Loader2,
  Send,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, {
  StatusResponse,
  StatusComponent,
  StatusIncident,
} from "@/lib/api";

const statusConfig: Record<
  string,
  { label: string; color: string; icon: typeof CheckCircle2; bg: string }
> = {
  operational: {
    label: "Operational",
    color: "text-emerald-400",
    icon: CheckCircle2,
    bg: "bg-emerald-500",
  },
  degraded: {
    label: "Degraded Performance",
    color: "text-yellow-400",
    icon: AlertTriangle,
    bg: "bg-yellow-500",
  },
  partial_outage: {
    label: "Partial Outage",
    color: "text-orange-400",
    icon: MinusCircle,
    bg: "bg-orange-500",
  },
  major_outage: {
    label: "Major Outage",
    color: "text-red-400",
    icon: XCircle,
    bg: "bg-red-500",
  },
};

const incidentStatusLabels: Record<string, string> = {
  investigating: "Investigating",
  identified: "Identified",
  monitoring: "Monitoring",
  resolved: "Resolved",
};

const reportCategories = [
  { value: "builds", label: "Builds" },
  { value: "dashboard", label: "Dashboard" },
  { value: "api", label: "API" },
  { value: "billing", label: "Billing" },
  { value: "other", label: "Other" },
];

function OverallStatus({ status }: { status: string }) {
  const config = statusConfig[status] || statusConfig.operational;
  const Icon = config.icon;

  return (
    <div className="flex flex-col items-center gap-3 py-8">
      <Icon className={cn("size-10", config.color)} />
      <h2 className="text-xl font-semibold">
        {config.label === "Operational"
          ? "All systems operational"
          : config.label}
      </h2>
    </div>
  );
}

function UptimeBar({ history }: { history: StatusComponent["history"] }) {
  return (
    <div className="flex gap-[2px]">
      {history.map((day, i) => {
        const config = statusConfig[day.status] || statusConfig.operational;
        return (
          <div
            key={i}
            className={cn(
              "h-8 flex-1 rounded-[2px] transition-opacity hover:opacity-80 cursor-default",
              config.bg
            )}
            title={`${day.date}: ${day.uptime_pct.toFixed(1)}% uptime`}
          />
        );
      })}
    </div>
  );
}

function ComponentRow({ component }: { component: StatusComponent }) {
  const config =
    statusConfig[component.current_status] || statusConfig.operational;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <CheckCircle2 className={cn("size-4", config.color)} />
          <span className="text-sm font-medium">{component.name}</span>
        </div>
        <span className={cn("text-sm font-medium", config.color)}>
          {component.uptime_pct.toFixed(1)}% uptime
        </span>
      </div>
      <UptimeBar history={component.history} />
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>90 days ago</span>
        <span>Today</span>
      </div>
    </div>
  );
}

function IncidentTimeline({ incidents }: { incidents: StatusIncident[] }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (incidents.length === 0) {
    return (
      <div className="flex items-center gap-3 py-4">
        <CheckCircle2 className="size-5 text-emerald-400" />
        <span className="text-sm text-muted-foreground">
          No incidents reported in the last 90 days
        </span>
      </div>
    );
  }

  const grouped: Record<string, StatusIncident[]> = {};
  for (const inc of incidents) {
    const month = new Date(inc.created_at).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
    if (!grouped[month]) grouped[month] = [];
    grouped[month].push(inc);
  }

  return (
    <div className="space-y-8">
      {Object.entries(grouped).map(([month, incs]) => (
        <div key={month}>
          <h3 className="mb-4 border-b border-border pb-2 text-base font-semibold">
            {month}
          </h3>
          <div className="space-y-3">
            {incs.map((inc) => {
              const isExpanded = expandedId === inc.id;
              const severityColor =
                inc.severity === "critical"
                  ? "text-red-400"
                  : inc.severity === "major"
                  ? "text-orange-400"
                  : "text-yellow-400";

              return (
                <div key={inc.id} className="rounded-xl border border-border">
                  <button
                    onClick={() =>
                      setExpandedId(isExpanded ? null : inc.id)
                    }
                    className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/30 rounded-xl"
                  >
                    <AlertTriangle
                      className={cn("size-4 shrink-0", severityColor)}
                    />
                    <span className="flex-1 text-sm font-medium">
                      {inc.title}
                    </span>
                    {isExpanded ? (
                      <ChevronDown className="size-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="size-4 text-muted-foreground" />
                    )}
                  </button>

                  {isExpanded && inc.updates.length > 0 && (
                    <div className="border-t border-border px-4 py-4">
                      <div className="relative space-y-5 pl-6">
                        <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border" />
                        {inc.updates.map((update) => (
                          <div key={update.id} className="relative">
                            <div
                              className={cn(
                                "absolute -left-6 top-1 size-3.5 rounded-full border-2 border-background",
                                update.status === "resolved"
                                  ? "bg-emerald-500"
                                  : "bg-muted-foreground"
                              )}
                            />
                            <div>
                              <div className="flex items-center gap-2">
                                <span
                                  className={cn(
                                    "rounded-full border px-2 py-0.5 text-xs font-medium",
                                    update.status === "resolved"
                                      ? "border-emerald-500/30 text-emerald-400"
                                      : "border-border text-muted-foreground"
                                  )}
                                >
                                  {incidentStatusLabels[update.status] ||
                                    update.status}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(
                                    update.created_at
                                  ).toLocaleDateString("en-US", {
                                    month: "long",
                                    day: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </span>
                              </div>
                              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                                {update.message}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function ReportDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState("other");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.submitReport({ email, category, subject, description });
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || "Failed to submit report");
    } finally {
      setSubmitting(false);
    }
  }

  function handleClose() {
    setEmail("");
    setCategory("other");
    setSubject("");
    setDescription("");
    setSubmitted(false);
    setError(null);
    onClose();
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-lg rounded-2xl border border-border bg-card p-0 shadow-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex size-9 items-center justify-center rounded-xl bg-violet-500/10">
              <MessageSquarePlus className="size-4 text-violet-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold">Report an Issue</h3>
              <p className="text-xs text-muted-foreground">
                Let us know what&apos;s happening
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {submitted ? (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <CheckCircle2 className="size-10 text-emerald-400" />
              <h4 className="text-lg font-semibold">Report Submitted</h4>
              <p className="text-sm text-muted-foreground max-w-xs">
                Thank you for reporting this issue. Our team has been notified
                and will investigate as soon as possible.
              </p>
              <Button onClick={handleClose} variant="outline" className="mt-2">
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="report-email">Your Email</Label>
                  <Input
                    id="report-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="report-category">Category</Label>
                  <select
                    id="report-category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  >
                    {reportCategories.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="report-subject">Subject</Label>
                <Input
                  id="report-subject"
                  placeholder="Brief summary of the issue"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="report-description">Description</Label>
                <textarea
                  id="report-description"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm min-h-[120px] resize-none focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500"
                  placeholder="Describe what you're experiencing. Include any error messages, steps to reproduce, or other details that might help us investigate."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                />
              </div>

              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Send className="size-4" />
                  )}
                  Submit Report
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function StatusPage() {
  const [data, setData] = useState<StatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    api
      .getStatus()
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="border-b border-border/40 bg-background/60 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-6">
          <Link href="/">
            <Image src="/logo.svg" alt="BuildMagic" width={220} height={65} className="h-10 w-auto" />
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setReportOpen(true)}
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              Report an issue
            </button>
            <Link
              href="/docs"
              className="rounded-lg border border-border/60 px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground hover:bg-muted/30"
            >
              Documentation
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="mx-auto max-w-3xl px-6 py-8">
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="size-6 animate-spin rounded-full border-2 border-muted-foreground/20 border-t-violet-500" />
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-border bg-card p-8 text-center">
            <XCircle className="mx-auto size-10 text-red-400" />
            <p className="mt-3 text-muted-foreground">
              Unable to load status: {error}
            </p>
          </div>
        ) : data ? (
          <>
            {/* Overall status */}
            <div className="rounded-2xl border border-border bg-card">
              <OverallStatus status={data.overall_status} />
            </div>

            {/* Components */}
            <div className="mt-8 space-y-8">
              {data.components.map((component) => (
                <ComponentRow key={component.id} component={component} />
              ))}
            </div>

            {/* Live health */}
            {data.live_health && (
              <div className="mt-10">
                <h3 className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Live Health Checks
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(data.live_health).map(([name, status]) => {
                    const isOk = status === "operational";
                    return (
                      <div
                        key={name}
                        className="flex items-center gap-2.5 rounded-xl border border-border px-4 py-3"
                      >
                        <div
                          className={cn(
                            "size-2 rounded-full",
                            isOk ? "bg-emerald-500" : "bg-red-500"
                          )}
                        />
                        <span className="text-sm capitalize">{name}</span>
                        <span
                          className={cn(
                            "ml-auto text-xs font-medium",
                            isOk ? "text-emerald-400" : "text-red-400"
                          )}
                        >
                          {isOk ? "OK" : "Down"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Incidents */}
            <div className="mt-12">
              <h3 className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Recent Incidents
              </h3>
              <IncidentTimeline incidents={data.incidents} />
            </div>
          </>
        ) : null}
      </div>

      {/* Footer */}
      <footer className="mt-16 border-t border-border/40">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-6">
          <span className="text-xs text-muted-foreground">
            BuildMagic System Status
          </span>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setReportOpen(true)}
              className="text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              Report an issue
            </button>
            <Link
              href="/"
              className="text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              Home
            </Link>
            <Link
              href="/docs"
              className="text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              Docs
            </Link>
          </div>
        </div>
      </footer>

      {/* Report dialog */}
      <ReportDialog open={reportOpen} onClose={() => setReportOpen(false)} />
    </div>
  );
}
